/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AIStarsIcon,
  Button,
  Div,
  ErrorToast,
  H3,
  Icon,
  Img,
  LoadingDots,
  Modal,
  ModalPortal,
  P,
  generateMenuAiImage,
  lightTheme,
  queryKeys,
} from '@vgrubs/components';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  AspectRatioPicker,
  AspectRatioValue,
  DEFAULT_ASPECT_RATIO,
  toAspectRatioDto,
} from './AspectRatioPicker';
import { AIStarsIllustration } from '../MenuAIImprove/menuAiGlow.styled';
import { DimensionPresets } from './DimensionPresets';
import { ImageLibraryModal } from './ImageLibraryModal';
import { PromptTextarea } from './PromptTextarea';
import { SampleImagesInput } from './SampleImagesInput';

/**
 * Sub-flow for the item add/edit form: collect a prompt + sample images and
 * call the synchronous generate-image endpoint. On accept the generated image
 * is fetched into a Blob and handed back via `onAccept`, so the existing item
 * save path (FormData multipart) uploads it with no backend change.
 */
export const GenerateImageModal = ({
  isOpen,
  closeModal,
  itemName,
  itemDescription,
  onAccept,
}: {
  isOpen: boolean;
  closeModal: () => void;
  itemName?: string;
  itemDescription?: string;
  onAccept: (image: Blob) => void;
}) => {
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const queryClient = useQueryClient();

  const [prompt, setPrompt] = useState('');
  const [ratio, setRatio] = useState<AspectRatioValue>(DEFAULT_ASPECT_RATIO);
  const [sampleImageUrls, setSampleImageUrls] = useState<string[]>([]);
  const [generating, setGenerating] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Reset only on close — "Regenerate" must keep the ratio selection.
  useEffect(() => {
    if (!isOpen) {
      setPrompt('');
      setRatio(DEFAULT_ASPECT_RATIO);
      setSampleImageUrls([]);
      setGenerating(false);
      setAccepting(false);
      setResultUrl(null);
      setLibraryOpen(false);
    }
  }, [isOpen]);

  const handleGenerate = async () => {
    // Belt-and-braces on top of the button's own click-blocking: a second call
    // would leave a stale request racing the first one to set `resultUrl`.
    if (generating) return;
    try {
      setGenerating(true);
      const { data } = await generateMenuAiImage(locationId, {
        itemName: itemName || undefined,
        itemDescription: itemDescription || undefined,
        prompt: prompt.trim() || undefined,
        sampleImageUrls: sampleImageUrls.length ? sampleImageUrls : undefined,
        ...toAspectRatioDto(ratio),
      });
      setResultUrl(data.data.imageUrl);
      // The new image joins the library — refresh the gallery so it shows up.
      queryClient.invalidateQueries({
        queryKey: queryKeys.admin.menuAiImages.all,
      });
    } catch (error) {
      ErrorToast('Failed to generate image.');
    } finally {
      setGenerating(false);
    }
  };

  // Picking a library image reuses the generated-image preview + accept flow:
  // drop the chosen url into `resultUrl` so the user confirms with "Use this
  // image" (which fetches it into a Blob), and no generation call is made.
  // The library stays open UNDERNEATH the preview (which sits above it), so
  // closing the preview returns to the gallery rather than the form.
  const handleUseLibraryImage = (url: string) => {
    setResultUrl(url);
  };

  const handleAccept = async () => {
    if (!resultUrl) return;
    try {
      setAccepting(true);
      const blob = await (await fetch(resultUrl)).blob();
      onAccept(blob);
      closeModal();
    } catch (error) {
      ErrorToast('Failed to attach the generated image.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      <Modal
        // Hidden while the gallery (libraryOpen) or the image preview
        // (resultUrl) is open — those sit on top; the form re-appears only once
        // both are closed.
        isOpen={isOpen && !resultUrl && !libraryOpen}
        closeModal={closeModal}
        header="Generate image with AI"
        subtitle={itemName}
        logo={false}
        textAlign="left"
        zIndex={120}
        glow={generating}
        $numOfFloatingButtons={2}
        footer={
          <Div $flex $column $gap="12px" $width="100%">
            <Button
              $selected
              $gray
              $w="100%"
              disabled={generating}
              onClick={() => setLibraryOpen(true)}
            >
              Browse generated images
            </Button>
            <Button
              $blueGradient
              $selected
              $w="100%"
              $loading={generating}
              onClick={handleGenerate}
            >
              <Div $flex $center $gap="8px">
                <Icon>
                  <AIStarsIcon />
                </Icon>
                Generate
              </Div>
            </Button>
          </Div>
        }
      >
        <Div $flex $column $gap="20px">
          <PromptTextarea
            label="Style guidance"
            placeholder="e.g. top-down shot, neutral background"
            value={prompt}
            onChange={({ target }) => setPrompt(target.value)}
            $textareaHeight="120"
            expandTitle="Edit style guidance"
          />
          <DimensionPresets
            value={ratio}
            onSelect={setRatio}
            disabled={generating}
          />
          <AspectRatioPicker
            value={ratio}
            onChange={setRatio}
            disabled={generating}
          />
          {/* While generating the add button is hidden — with no images left
              to show, drop the whole section (label included) instead of
              leaving an orphaned "Sample images" heading. */}
          {!(generating && sampleImageUrls.length === 0) && (
            <div>
              <P $mb="8">Sample images (optional)</P>
              <SampleImagesInput
                locationId={locationId}
                brandId={brandId}
                urls={sampleImageUrls}
                onChange={setSampleImageUrls}
                max={4}
                disabled={generating}
              />
            </div>
          )}

          {generating && (
            <P $center $color={lightTheme.lightGray} $m="0">
              Generating… this can take up to ~20 seconds.
            </P>
          )}
        </Div>
      </Modal>

      <ImageLibraryModal
        isOpen={libraryOpen}
        closeModal={() => setLibraryOpen(false)}
        initialSearch={itemName}
        onSelect={handleUseLibraryImage}
      />

      {/* Generated / picked image opens in its own preview modal (on top of the
          hidden form) with the accept actions, instead of showing inline. */}
      <ModalPortal
        isOpen={!!resultUrl}
        closeModal={() => setResultUrl(null)}
        header={itemName}
        zIndex={140}
        width="800px"
        minHeight="80vh"
        maxHeight="80vh"
        glow={generating}
        footer={
          <Div $flex $gap="16px" $width="100%">
            <Button
              $blueGradient
              $selected
              $w="100%"
              $loading={generating}
              onClick={handleGenerate}
            >
              <Div $flex $center $gap="8px">
                <Icon>
                  <AIStarsIcon />
                </Icon>
                Regenerate
              </Div>
            </Button>
            <Button
              $selected
              $w="100%"
              $loading={accepting}
              disabled={generating}
              onClick={handleAccept}
            >
              Use this image
            </Button>
          </Div>
        }
      >
        {/* Fill the modal's content box so $center centers the image
            vertically. 80vh scrollable − 24px top pad − 170px footer clearance
            (the modal's padding-bottom for a 2-button footer). */}
        <Div $flex $center $minHeight="calc(80vh - 114px)">
          {generating ? (
            <Div $flex $column $center $gap="16px">
              <AIStarsIllustration>
                <AIStarsIcon />
              </AIStarsIllustration>
              <H3 $m="0" $center $spanColor={lightTheme.darkGray}>
                Generating image
                <LoadingDots />
              </H3>
            </Div>
          ) : (
            <Img src={resultUrl ?? ''} $maxWidth="100%" $maxHeight="65vh" />
          )}
        </Div>
      </ModalPortal>
    </>
  );
};
